/** @jsx jsx */
import React, {
	useState,
	useCallback,
	useMemo,
	useEffect,
	type KeyboardEvent,
	useRef,
} from 'react';
import { styled, jsx } from '@compiled/react';

import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useLazyLoadQuery, usePaginationFragment, useFragment } from 'react-relay';
import type { KeyType } from 'react-relay/relay-hooks/helpers';
import type { OperationType } from 'relay-runtime';
import { useSuspenselessRefetch } from '@jira/issue__use-suspenseless-refetch/src/index.tsx';
import { TEAM_MANAGED_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { layers } from '@jira/platform__ui-layers';
import { xcss, Inline, Box } from '@atlaskit/primitives';
import Select, { PopupSelect, type FormatOptionLabelMeta } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import type { AkSelectStyles } from '@atlassian/jira-common-components-picker/src/model.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { SEARCH_DEBOUNCE_TIMEOUT } from '@atlassian/jira-issue-field-constants/src/index.tsx';
import { filterOptionByLabelAndFilterValues } from '@atlassian/jira-issue-field-select-base/src/ui/filter-options-by-label-and-filter-values/index.tsx';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base/src/ui/react-select-styles/styled.tsx';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import type { issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithFieldOptionsFragment_queryFragmentRef$key as IssueTypeFragment } from '@atlassian/jira-relay/src/__generated__/issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithFieldOptionsFragment_queryFragmentRef.graphql';
import type { issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_issueTypesFragmentRef$key as IssueTypeConnectionFragment } from '@atlassian/jira-relay/src/__generated__/issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_issueTypesFragmentRef.graphql';
import type { issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_permissionsFragmentRef$key as PermissionsFragment } from '@atlassian/jira-relay/src/__generated__/issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_permissionsFragmentRef.graphql';
import issueTypeSearchRefetchQuery, {
	type issueType_SearchRefetchQuery as IssueTypeRefetchQuery,
} from '@atlassian/jira-relay/src/__generated__/issueType_SearchRefetchQuery.graphql';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';
import { MenuFooter } from './menu-footer/index.tsx';
import { useManageIssueTypeCallbacks } from './menu-footer/useManageIssueTypeCallbacks.tsx';
import { Trigger } from './trigger/index.tsx';
import type {
	Option,
	IssueTypeEditViewWithFieldOptionsFragmentProps,
	IssueTypeEditViewProps,
	IssueTypeEditViewWithIssueTypeConnectionProps,
	NullableOption,
} from './types.tsx';

const iconOnlySelectStyles: AkSelectStyles = {
	menuPortal: (base) => ({ ...base, zIndex: layers.modal }),
	menu: (base) => ({
		...base,
		zIndex: layers.modal,
		minWidth: '120px',
		maxWidth: '440px',
		width: 'auto',
		overflowY: 'auto',
	}),
	control: (base) => ({
		...base,
		'&:hover': {
			borderColor: 'transparent',
		},
	}),
};

/**
 * IssueTypeEditViewWithFieldOptionsFragment is a version of the edit view that allows
 * the passing of a fragment for experiences like issue transition screen that might want to group fetch data on mount
 * instead of having a separate network request for it
 *
 * @param props [IssueTypeEditViewWithFieldOptionsFragmentProps](./types.tsx)
 */
export const IssueTypeEditViewWithFieldOptionsFragment = ({
	queryFragmentRef,
	fieldId,
	filterOptionsById = null,
	...props
}: IssueTypeEditViewWithFieldOptionsFragmentProps): JSX.Element => {
	const { data: issueTypesSearchData, refetch: refetchSuggestions } = usePaginationFragment<
		IssueTypeRefetchQuery,
		IssueTypeFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithFieldOptionsFragment_queryFragmentRef on Query
			@refetchable(queryName: "issueType_SearchRefetchQuery")
			@argumentDefinitions(
				id: { type: "ID!" }
				searchBy: { type: "String", defaultValue: "" }
				first: { type: "Int", defaultValue: 1000 }
				after: { type: "String", defaultValue: null }
				filterById: { type: "JiraFieldOptionIdsFilterInput" }
			) {
				node(id: $id) {
					... on JiraIssueTypeField {
						issueTypes(searchBy: $searchBy, first: $first, after: $after, filterById: $filterById)
							@connection(key: "issueType_issueFieldIssueTypeEditviewFull_issueTypes") {
							edges {
								__typename
							}
							...issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_issueTypesFragmentRef
						}
					}
				}
			}
		`,
		queryFragmentRef,
	);

	const onGetRefetchVariables = useCallback(
		(searchBy: string) => ({ id: fieldId, searchBy, filterById: filterOptionsById }),
		[fieldId, filterOptionsById],
	);

	return (
		<IssueTypeEditViewWithIssueTypeConnection
			{...props}
			issueTypesFragmentRef={issueTypesSearchData.node?.issueTypes ?? null}
			onGetRefetchVariables={onGetRefetchVariables}
			refetchQuery={issueTypeSearchRefetchQuery}
			onRefetch={refetchSuggestions}
		/>
	);
};

/**
 * IssueTypeEditViewWithIssueTypeConnection is a version of the edit view that allows
 * the passing of a issue type connection fragment for experiences like GIC that might want to group fetch data on mount
 * instead of having a separate network request for it
 *
 * @param props [IssueTypeEditViewWithIssueTypeConnectionProps](./types.tsx)
 */
export const IssueTypeEditViewWithIssueTypeConnection = <
	TQuery extends OperationType,
	TKey extends KeyType | null,
>({
	autoFocus = false,
	issueTypesFragmentRef,
	onChange: onChangeProp,
	onGetRefetchVariables,
	setIsEditing,
	value,
	noOptionsMessage,
	loadingMessage,
	isInvalid,
	isDisabled = false,
	inputId,
	menuPosition,
	menuPlacement,
	refetchQuery,
	onRefetch,
	openMenuOnFocus,
	spacing,
	stopPropagationOfMenuEscape,
	iconsOnly = false,
	appearance = 'default',
	isSearchable = true,
	placeholder,
	manageIssueTypeProps,
	isManageIssueTypesModalOpen,
	projectFragmentRef,
	renderAsPopupSelect = false,
}: IssueTypeEditViewWithIssueTypeConnectionProps<TQuery, TKey>): JSX.Element => {
	const hasAutoFocus = autoFocus && (!renderAsPopupSelect || !fg('jifjam2_icc_fix'));

	const [menuIsOpen, setMenuIsOpen] = useState<boolean>(hasAutoFocus);
	const triggerRef = useRef<HTMLElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();
	const getNoOptionsMessage = useCallback(
		(): string => noOptionsMessage ?? formatMessage(messages.empty),
		[formatMessage, noOptionsMessage],
	);

	const getLoadingMessage = useCallback(
		(): string => loadingMessage ?? formatMessage(messages.loading),
		[formatMessage, loadingMessage],
	);
	const defaultPlaceholder = formatMessage(
		fg('jira-issue-terminology-update-m4')
			? messages.placeholderIssueTerminologyRefreshM4
			: messages.placeholder,
	);

	// #region Relay
	// suggestions fragment with pagination and refetch
	const data = useFragment<IssueTypeConnectionFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_issueTypesFragmentRef on JiraIssueTypeConnection
			@argumentDefinitions(
				isInlineManageWorkTypesConfigNinIccEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-nin-inline-manage-work-type-config.relayprovider"
				}
			) {
				edges {
					node {
						value: id
						label: name
						avatar {
							small
						}
						issueTypeId @include(if: $isInlineManageWorkTypesConfigNinIccEnabled)
					}
				}
			}
		`,
		issueTypesFragmentRef,
	);

	// manage issue types permission fragment
	const permissionsData = useFragment<PermissionsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_permissionsFragmentRef on JiraProject {
				admin: action(type: EDIT_PROJECT_CONFIG) {
					canPerform
				}
				projectStyle
				projectType
			}
		`,
		projectFragmentRef ?? null,
	);

	// Check if user has permission to manage issue types
	// If fragment wasn't spread by consumer, permissionsData will be null and we won't show the feature
	const canManageIssueTypes = permissionsData?.admin?.canPerform ?? false;
	// #endregion

	// #region Common state (might be handy for other components)
	const [searchByString, setSearchByString] = useState<string>('');
	// #endregion

	// #region Debounced suspensless refetch helpers
	const [searchSuspenselessRefetch, isLoading, lastFetchError] = useSuspenselessRefetch(
		refetchQuery,
		onRefetch,
	);

	const [searchDebouncedSuspenselessRefetch] = useDebouncedCallback(
		searchSuspenselessRefetch,
		SEARCH_DEBOUNCE_TIMEOUT,
	);

	useEffect(() => {
		if (hasAutoFocus) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
			searchSuspenselessRefetch(onGetRefetchVariables(searchByString));
		}
	}, [searchSuspenselessRefetch, autoFocus, searchByString, onGetRefetchVariables, hasAutoFocus]);

	const onKeyDown = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (stopPropagationOfMenuEscape && menuIsOpen && e.key === 'Escape') {
				e.stopPropagation();
			}
		},
		[menuIsOpen, stopPropagationOfMenuEscape],
	);

	// #region Common callbacks for the selector
	const openMenuOld = () => setMenuIsOpen(true);
	const openMenu = useCallback(() => {
		setIsEditing?.(true);
		setMenuIsOpen(true);

		if (fg('work_type_inline_config_nin_icc_fg')) {
			fireUIAnalytics(createAnalyticsEvent({}), 'select clicked', 'issueTypeSelect', {
				numIssueTypes: data?.edges?.length ?? undefined,
			});
		}
	}, [setIsEditing, setMenuIsOpen, createAnalyticsEvent, data?.edges?.length]);

	const closeMenuOld = () => setMenuIsOpen(false);
	const closeMenu = () => {
		setMenuIsOpen(false);

		if (fg('work_type_inline_config_nin_icc_fg')) {
			fireUIAnalytics(createAnalyticsEvent({}), 'select closed', 'issueTypeSelect', {
				numIssueTypes: data?.edges?.length ?? undefined,
			});
		}

		setTimeout(() => {
			setIsEditing?.(false);
			triggerRef.current?.focus();
		}, 0);
	};

	const onSelectFocus = useCallback((): void => {
		searchSuspenselessRefetch(onGetRefetchVariables(searchByString));
	}, [searchSuspenselessRefetch, searchByString, onGetRefetchVariables]);

	const onSearchByStringChangeFunction = useCallback(
		(newSearchByString: string): void => {
			setSearchByString(newSearchByString);
			searchDebouncedSuspenselessRefetch(onGetRefetchVariables(newSearchByString || ''));
		},
		[onGetRefetchVariables, searchDebouncedSuspenselessRefetch],
	);

	const onChange = useCallback(
		(newOption: NullableOption) => {
			onChangeProp?.(newOption);
			fireUIAnalytics(createAnalyticsEvent({}), 'select changed', 'issueTypeSelect', {
				numIssueTypes: data?.edges?.length ?? undefined,
			});
		},
		[createAnalyticsEvent, onChangeProp, data?.edges?.length],
	);
	// #endregion

	// #region Transform options data from relay to AK Select
	const defaultFailedOption = useMemo(
		() => ({
			label: formatMessage(messages.failedFetch),
			value: '',
			avatar: {
				small: '',
			},
			issueTypeId: undefined,
			isDisabled: true,
		}),
		[formatMessage],
	);

	const renderIconOnlyOption = (
		option: Option,
		formatOptionLabelMeta: FormatOptionLabelMeta<Option>,
	) => (
		<OptionWrapper>
			<Inline xcss={inlineIconStyle}>
				<img src={option.avatar?.small || ''} alt={option.label} width={16} height={16} />
			</Inline>
			{formatOptionLabelMeta.context !== 'value' && <Box>{option.label}</Box>}
		</OptionWrapper>
	);

	const renderIconWithTextOption = (option: Option) => (
		<OptionWrapper>
			<ImgSpan>
				<img
					src={option.avatar?.small || ''}
					alt={fg('work_type_inline_config_nin_icc_fg') ? '' : option.label}
					width={16}
					height={16}
				/>
			</ImgSpan>
			<>{option.label}</>
		</OptionWrapper>
	);

	const selectFieldOptions: Option[] = useMemo(() => {
		// checks for query errors
		if (lastFetchError != null) {
			return [defaultFailedOption];
		}
		return (
			data?.edges
				?.map((edge) => edge?.node)
				.filter(Boolean)
				.map(({ label: optionLabel, value: optionValue, avatar, issueTypeId }) => ({
					label: optionLabel ?? '',
					value: optionValue,
					avatar,
					issueTypeId: fg('work_type_inline_config_nin_icc_fg')
						? (issueTypeId ?? undefined)
						: undefined,
				})) ?? []
		);
	}, [lastFetchError, data, defaultFailedOption]);

	// #endregion

	const { onIssueTypeUpdated, onIssueTypeCreated } = fg('work_type_inline_config_nin_icc_fg')
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useManageIssueTypeCallbacks({
				onRefetch,
				onGetRefetchVariables,
				selectFieldOptions,
				onChange,
			})
		: {
				onIssueTypeUpdated: noop,
				onIssueTypeCreated: noop,
			};

	const selectedIssueTypeId = value?.issueTypeId;
	const shouldRenderFooter =
		manageIssueTypeProps && canManageIssueTypes && selectedIssueTypeId && lastFetchError == null;

	if (!renderAsPopupSelect || !fg('jifjam2_icc_fix')) {
		return (
			<Select
				aria-label={formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.labelIssueTerminologyRefreshM4
						: messages.label,
				)}
				formatOptionLabel={iconsOnly ? renderIconOnlyOption : renderIconWithTextOption}
				styles={iconsOnly ? iconOnlySelectStyles : defaultSelectStyles}
				value={value}
				options={selectFieldOptions}
				menuIsOpen={menuIsOpen}
				placeholder={
					placeholder && fg('jifjam_deliveroo_icc_changes') ? placeholder : defaultPlaceholder
				}
				noOptionsMessage={getNoOptionsMessage}
				loadingMessage={getLoadingMessage}
				filterOption={filterOptionByLabelAndFilterValues}
				onInputChange={onSearchByStringChangeFunction}
				onChange={fg('work_type_inline_config_nin_icc_fg') ? onChange : onChangeProp}
				openMenuOnFocus={openMenuOnFocus}
				onFocus={onSelectFocus}
				onKeyDown={onKeyDown}
				autoFocus={autoFocus}
				isInvalid={isInvalid}
				isDisabled={isDisabled}
				isLoading={isLoading && menuIsOpen}
				inputId={inputId}
				menuPosition={menuPosition}
				menuPlacement={menuPlacement}
				onMenuOpen={fg('work_type_inline_config_nin_icc_fg') ? openMenu : openMenuOld}
				onMenuClose={fg('work_type_inline_config_nin_icc_fg') ? closeMenu : closeMenuOld}
				spacing={spacing}
				appearance={appearance}
				isSearchable={isSearchable}
			/>
		);
	}

	return (
		<PopupSelect
			formatOptionLabel={renderIconWithTextOption}
			styles={defaultSelectStyles}
			value={value}
			options={selectFieldOptions}
			isOpen={isManageIssueTypesModalOpen ? false : undefined}
			noOptionsMessage={getNoOptionsMessage}
			loadingMessage={getLoadingMessage}
			filterOption={filterOptionByLabelAndFilterValues}
			onInputChange={onSearchByStringChangeFunction}
			onFocus={onSelectFocus}
			onChange={fg('work_type_inline_config_nin_icc_fg') ? onChange : onChangeProp}
			onKeyDown={onKeyDown}
			isDisabled={isDisabled}
			isLoading={isLoading && menuIsOpen}
			inputId={inputId}
			onMenuOpen={openMenu}
			onMenuClose={closeMenu}
			spacing={spacing}
			appearance={appearance}
			target={({ ref, ...triggerProps }) => (
				<Trigger
					{...triggerProps}
					value={value}
					triggerRef={mergeRefs(ref, triggerRef)}
					isOpen={menuIsOpen}
					isDisabled={isDisabled}
				/>
			)}
			footer={
				shouldRenderFooter && fg('work_type_inline_config_nin_icc_fg') ? (
					<MenuFooter
						{...manageIssueTypeProps}
						selectedIssueTypeId={selectedIssueTypeId}
						onIssueTypeUpdated={onIssueTypeUpdated}
						onIssueTypeCreated={onIssueTypeCreated}
						options={selectFieldOptions}
						menuIsOpen={menuIsOpen}
						isSimplifiedProject={permissionsData?.projectStyle === TEAM_MANAGED_PROJECT}
						projectType={permissionsData?.projectType}
					/>
				) : undefined
			}
			popperProps={{
				strategy: menuPosition,
				modifiers: [
					{
						name: 'preventOverflow',
						enabled: false,
					},
				],
			}}
			shouldCloseMenuOnTab={!shouldRenderFooter}
			closeMenuOnSelect
			isSearchable={false}
		/>
	);
};

/**
 * Simple use case variant which handles its own fetching of suggestions data.
 * Use the IssueTypeEditViewWithFieldOptionsFragment variant if you want to handle
 * prefilling the suggestions data as part of a larger query instead of on mount / on focus.
 *
 * @param props [IssueTypeEditViewProps](./types.tsx)
 */
export const IssueTypeEditView = (props: IssueTypeEditViewProps): JSX.Element => {
	const suggestionsData = useLazyLoadQuery<IssueTypeRefetchQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query issueType_issueTypeSearchQuery($id: ID!, $filterById: JiraFieldOptionIdsFilterInput) {
				...issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithFieldOptionsFragment_queryFragmentRef
					@arguments(id: $id, searchBy: "", filterById: $filterById)
			}
		`,
		{ id: props.fieldId, filterById: props.filterOptionsById },
		{ fetchPolicy: 'store-only' },
	);

	if (fg('work_type_inline_config_nin_icc_fg')) {
		return (
			<JSErrorBoundary
				packageName="jiraPlatformIssueTypeEditView"
				id="jira-platform-issue-type-edit-view"
				fallback="flag"
			>
				<ModalContextProvider>
					<IssueTypeEditViewWithFieldOptionsFragment
						{...props}
						queryFragmentRef={suggestionsData}
					/>
				</ModalContextProvider>
			</JSErrorBoundary>
		);
	}

	return (
		<IssueTypeEditViewWithFieldOptionsFragment {...props} queryFragmentRef={suggestionsData} />
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionWrapper = styled.span({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImgSpan = styled.span({
	display: 'flex',
	paddingRight: token('space.100'),
});

const inlineIconStyle = xcss({
	marginRight: 'space.100',
});
